import React, { createContext, useContext, useState, useEffect } from 'react';
import { clearAllStorage } from '../utils/storageUtils';
import { authStorage, migrateLegacyAuthFromLocalStorage } from '../utils/authStorage';

interface AuthState {
  isAuthenticated: boolean;
  user: any;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string) => Promise<boolean>;
  updatePassword: (oldPassword: string, newPassword: string) => Promise<boolean>;
  updateEmail: (newEmail: string) => Promise<boolean>;
  getAuthHeaders: () => { [key: string]: string };
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 初回マウント時に旧 localStorage 保管の auth を sessionStorage に移行する。
  // 既存ユーザーがアップデート後に強制ログアウトされないようにする。
  useEffect(() => {
    migrateLegacyAuthFromLocalStorage();
  }, []);

  const [authState, setAuthState] = useState<AuthState>(() => {
    const savedAuth = authStorage.get<AuthState>();
    return savedAuth || {
      isAuthenticated: false,
      user: null
    };
  });

  const login = async (userId: string, password: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        switch (response.status) {
          case 401:
            throw new Error(errorData.detail || 'ユーザーIDまたはパスワードが間違っています');
          case 403:
            throw new Error('このアカウントは退会済みです');
          case 429:
            throw new Error('ログイン試行回数が多すぎます。しばらくしてから再度お試しください');
          default:
            throw new Error(errorData.detail || 'ログインに失敗しました');
        }
      }

      const data = await response.json();

      const newAuthState: AuthState = {
        isAuthenticated: true,
        user: {
          ...data.data.user,
          api_key: data.data.user.api_key
        }
      };

      const saveSuccess = authStorage.set(newAuthState);

      if (saveSuccess) {
        setAuthState(newAuthState);
      } else {
        throw new Error('認証状態の保存に失敗しました');
      }

      return true;
    } catch (error) {
      console.error('Login Error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        throw new Error('登録に失敗しました');
      }

      await response.json();
      return true;
    } catch (error) {
      console.error('登録エラー:', error);
      return false;
    }
  };

  const updatePassword = async (oldPassword: string, newPassword: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: authState.user?.user_id,
          old_password: oldPassword,
          new_password: newPassword
        })
      });

      if (!response.ok) {
        throw new Error('パスワード更新に失敗しました');
      }

      await response.json();
      return true;

    } catch (error) {
      console.error('パスワード更新エラー:', error);
      return false;
    }
  };

  const updateEmail = async (newEmail: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/change-email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: authState.user?.user_id,
          new_email: newEmail
        })
      });

      if (!response.ok) {
        throw new Error('メールアドレス更新に失敗しました');
      }

      await response.json();
      const newUserName = newEmail.split('@')[0];

      const newAuthState: AuthState = {
        ...authState,
        user: {
          ...authState.user,
          email: newEmail,
          user_name: newUserName
        }
      };
      authStorage.set(newAuthState);
      setAuthState(newAuthState);
      return true;

    } catch (error) {
      console.error('メールアドレス更新エラー:', error);
      return false;
    }
  };

  const getAuthHeaders = () => {
    const auth = authStorage.get<AuthState>();
    return {
      'Content-Type': 'application/json',
      'X-API-Key': auth?.user?.api_key || ''
    };
  };

  const logout = () => {
    try {
      authStorage.clear();
      clearAllStorage().catch(error => {
        console.error('ストレージクリア中にエラーが発生しました:', error);
      });

      setAuthState({
        isAuthenticated: false,
        user: null
      });
    } catch (error) {
      console.error('ログアウト中にエラーが発生しました:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, register, updatePassword, updateEmail, getAuthHeaders, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
